import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';

const IDB_NAME = 'counter-pump-skins';
const IDB_STORE = 'glb-cache';
const IDB_VERSION = 1;

const DRACO_CDN_URL = 'https://www.gstatic.com/draco/versioned/decoders/1.5.7/';
const DRACO_CDN_TIMEOUT_MS = 10_000;

export class SkinLoader {
  private gltfLoader: GLTFLoader;
  private loadedModels = new Map<number, THREE.Group>();
  private loadingManager: THREE.LoadingManager;
  private db: IDBDatabase | null = null;
  private dracoReady: Promise<void>;

  constructor() {
    this.loadingManager = new THREE.LoadingManager();
    this.gltfLoader = new GLTFLoader(this.loadingManager);

    // Initialize DRACO with a CDN reachability check (H35/M40)
    this.dracoReady = this.initDracoWithTimeout();
  }

  /**
   * Verify the DRACO CDN is reachable before configuring the decoder.
   * Times out after DRACO_CDN_TIMEOUT_MS; on failure, DRACO decoding is disabled
   * and non-DRACO GLBs continue to work normally.
   */
  private async initDracoWithTimeout(): Promise<void> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), DRACO_CDN_TIMEOUT_MS);

    try {
      // HEAD request to check CDN reachability without downloading the full decoder
      const response = await fetch(DRACO_CDN_URL + 'draco_decoder.js', {
        method: 'HEAD',
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        console.warn(`SkinLoader: DRACO CDN returned HTTP ${response.status}, disabling DRACO decoding`);
        return;
      }

      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath(DRACO_CDN_URL);
      this.gltfLoader.setDRACOLoader(dracoLoader);
    } catch (err) {
      clearTimeout(timeoutId);
      console.warn('SkinLoader: DRACO CDN unreachable (timeout or network error), disabling DRACO decoding', err);
    }
  }

  /** Initialize IndexedDB for caching. */
  async initCache(): Promise<void> {
    if (this.db) return;

    return new Promise<void>((resolve, reject) => {
      const request = indexedDB.open(IDB_NAME, IDB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(IDB_STORE)) {
          db.createObjectStore(IDB_STORE);
        }
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onerror = () => {
        console.warn('SkinLoader: IndexedDB init failed, proceeding without cache');
        resolve();
      };
    });
  }

  /** Load a skin GLB by path. Checks IndexedDB cache first. */
  async loadSkin(skinId: number, glbPath: string): Promise<THREE.Group> {
    // Ensure DRACO decoder is ready (or confirmed unavailable) before parsing
    await this.dracoReady;

    // Return already-loaded model
    const existing = this.loadedModels.get(skinId);
    if (existing) return existing;

    let gltf: { scene: THREE.Group };

    // Try loading from IndexedDB cache
    const cached = await this.idbGet(glbPath);
    if (cached) {
      try {
        gltf = await this.gltfLoader.parseAsync(cached, '');
      } catch {
        console.warn(`SkinLoader: cached parse failed for ${glbPath}, fetching fresh`);
        gltf = await this.fetchAndCache(glbPath);
      }
    } else {
      gltf = await this.fetchAndCache(glbPath);
    }

    const model = gltf.scene;
    this.prepareMeshes(model);
    this.loadedModels.set(skinId, model);
    return model;
  }

  /**
   * Preload every skin in the manifest.
   * Individual failures (missing GLB, DRACO unavailable, etc.) are caught and
   * logged; the method never rejects so a single bad asset cannot block boot.
   */
  async preloadAll(
    manifest: ReadonlyMap<number, { id: number; glbPath: string }>,
    onProgress?: (loaded: number, total: number) => void,
  ): Promise<void> {
    const entries = Array.from(manifest.values()).map(({ id, glbPath }) => ({ skinId: id, glbPath }));
    await this.loadMultiple(entries, onProgress);
  }

  /** Load multiple skins in parallel with progress callback. */
  async loadMultiple(
    skins: Array<{ skinId: number; glbPath: string }>,
    onProgress?: (loaded: number, total: number) => void,
  ): Promise<void> {
    const total = skins.length;
    let loaded = 0;

    const tasks = skins.map(async ({ skinId, glbPath }) => {
      try {
        await this.loadSkin(skinId, glbPath);
      } catch (err) {
        console.error(`SkinLoader: failed to load skin ${skinId} (${glbPath})`, err);
      }
      loaded++;
      onProgress?.(loaded, total);
    });

    await Promise.all(tasks);
  }

  /** Clone a previously loaded skin (using SkeletonUtils for skinned meshes). */
  cloneSkin(skinId: number): THREE.Group | null {
    const source = this.loadedModels.get(skinId);
    if (!source) return null;

    const cloned = SkeletonUtils.clone(source) as THREE.Group;
    this.prepareMeshes(cloned);

    // M27: Re-apply material customizations from source to clone.
    // SkeletonUtils.clone copies from the original unmodified geometry/skeleton,
    // but materials may have been customized (wear, tint, etc.) after initial load.
    this.copyMaterialCustomizations(source, cloned);

    return cloned;
  }

  /**
   * Copy material customizations from a source model to a cloned model.
   * Matches meshes by traversal order (same structure guaranteed by SkeletonUtils.clone).
   */
  private copyMaterialCustomizations(source: THREE.Group, clone: THREE.Group): void {
    const sourceMeshes: THREE.Mesh[] = [];
    const cloneMeshes: THREE.Mesh[] = [];

    source.traverse((child) => {
      if (child instanceof THREE.Mesh) sourceMeshes.push(child);
    });
    clone.traverse((child) => {
      if (child instanceof THREE.Mesh) cloneMeshes.push(child);
    });

    for (let i = 0; i < Math.min(sourceMeshes.length, cloneMeshes.length); i++) {
      const srcMat = sourceMeshes[i].material;
      if (Array.isArray(srcMat)) {
        cloneMeshes[i].material = srcMat.map((m) => m.clone());
      } else if (srcMat) {
        cloneMeshes[i].material = srcMat.clone();
      }
    }
  }

  /** Check if a skin is already loaded. */
  isLoaded(skinId: number): boolean {
    return this.loadedModels.has(skinId);
  }

  /** Dispose all loaded models. */
  dispose(): void {
    for (const model of this.loadedModels.values()) {
      model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry?.dispose();
          const material = child.material;
          if (Array.isArray(material)) {
            material.forEach((m) => this.disposeMaterial(m));
          } else if (material) {
            this.disposeMaterial(material);
          }
        }
      });
    }
    this.loadedModels.clear();

    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /** Fetch a GLB over HTTP, parse it, and cache the ArrayBuffer in IDB. */
  private async fetchAndCache(
    glbPath: string,
  ): Promise<{ scene: THREE.Group }> {
    // Fetch raw ArrayBuffer so we can cache it
    const response = await fetch(glbPath);
    if (!response.ok) {
      throw new Error(`SkinLoader: HTTP ${response.status} fetching ${glbPath}`);
    }
    const buffer = await response.arrayBuffer();

    // Cache in background (don't block loading)
    this.idbPut(glbPath, buffer).catch((err) =>
      console.warn('SkinLoader: IDB cache write failed', err),
    );

    return this.gltfLoader.parseAsync(buffer, '');
  }

  /** Enable shadows on all meshes in a model. */
  private prepareMeshes(root: THREE.Object3D): void {
    root.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
  }

  /** Dispose a single material and its textures. */
  private disposeMaterial(material: THREE.Material): void {
    // Dispose textures on standard-ish materials
    const mat = material as THREE.MeshStandardMaterial;
    mat.map?.dispose();
    mat.normalMap?.dispose();
    mat.roughnessMap?.dispose();
    mat.metalnessMap?.dispose();
    mat.aoMap?.dispose();
    mat.emissiveMap?.dispose();
    material.dispose();
  }

  /** Read an ArrayBuffer from IndexedDB. */
  private async idbGet(key: string): Promise<ArrayBuffer | null> {
    if (!this.db) return null;
    return new Promise((resolve) => {
      try {
        const tx = this.db!.transaction(IDB_STORE, 'readonly');
        const store = tx.objectStore(IDB_STORE);
        const req = store.get(key);
        req.onsuccess = () => resolve(req.result ?? null);
        req.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  }

  /** Write an ArrayBuffer to IndexedDB. */
  private async idbPut(key: string, value: ArrayBuffer): Promise<void> {
    if (!this.db) return;
    return new Promise((resolve, reject) => {
      try {
        const tx = this.db!.transaction(IDB_STORE, 'readwrite');
        const store = tx.objectStore(IDB_STORE);
        const req = store.put(value, key);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      } catch (err) {
        reject(err);
      }
    });
  }
}
